import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  HiOutlineDevicePhoneMobile,
  HiOutlineUserGroup,
  HiOutlineClock,
  HiOutlineArrowRight,
  HiOutlineBuildingLibrary,
  HiOutlineBanknotes,
  HiOutlineShieldCheck,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi2";

import classes from "./HomePage.module.css";
import { bankName, countryCode } from "../../util/config";
import s1 from "../../images/s1.jpg";
import s2 from "../../images/s2.jpg";
import s3 from "../../images/s3.jpg";

const HomePage = () => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  return (
    <div className={classes.homepage}>
      <section className={classes.hero}>
        <div className={classes.heroOverlay} />
        <div className={classes.heroInner}>
          <p className={classes.eyebrow}>Trusted digital banking</p>
          <h1 className={classes.heroTitle}>
            Banking that feels personal—secure, clear, always within reach
          </h1>
          <p className={classes.heroLead}>
            Manage accounts, move money, and plan ahead from any device. We have
            refined Online Banking so every journey—from everyday payments to
            life’s bigger moments—feels calm and in control.
          </p>

          <div className={classes.metrics}>
            <div className={classes.metric}>
              <span className={classes.metricValue}>24/7</span>
              <span className={classes.metricLabel}>Secure access</span>
            </div>
            <div className={classes.metric}>
              <span className={classes.metricValue}>FINMA</span>
              <span className={classes.metricLabel}>Regulated</span>
            </div>
            <div className={classes.metric}>
              <span className={classes.metricValue}>{countryCode}</span>
              <span className={classes.metricLabel}>Dedicated support</span>
            </div>
          </div>

          <ul className={classes.bullets}>
            <li>
              <span className={classes.bulletIcon} aria-hidden>
                <HiOutlineDevicePhoneMobile />
              </span>
              <span>
                Online Banking on mobile, tablet, or desktop—one experience,
                consistently designed.
              </span>
            </li>
            <li>
              <span className={classes.bulletIcon} aria-hidden>
                <HiOutlineUserGroup />
              </span>
              <span>
                Built with accessibility in mind, so more customers can bank
                independently and with confidence.
              </span>
            </li>
            <li>
              <span className={classes.bulletIcon} aria-hidden>
                <HiOutlineClock />
              </span>
              <span>
                Round-the-clock support for urgent queries and peace of mind
                when markets and schedules do not wait.
              </span>
            </li>
          </ul>

          <div className={classes.ctaRow}>
            <Link
              className={classes.ctaPrimary}
              to={isLoggedIn ? "/account" : "/login"}
            >
              {isLoggedIn ? "Go to your account" : "Sign in to Online Banking"}
              <HiOutlineArrowRight className={classes.ctaPrimaryIcon} aria-hidden />
            </Link>
            <div className={classes.quickLinks}>
              <Link to="/banking" className={classes.quickLink}>
                <HiOutlineBuildingLibrary aria-hidden />
                Banking
              </Link>
              <Link to="/borrowing" className={classes.quickLink}>
                <HiOutlineBanknotes aria-hidden />
                Borrowing
              </Link>
              <Link to="/insurance" className={classes.quickLink}>
                <HiOutlineShieldCheck aria-hidden />
                Insurance
              </Link>
              <Link to="/help" className={classes.quickLink}>
                <HiOutlineQuestionMarkCircle aria-hidden />
                Help
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className={classes.more}>
        <div className={classes.sectionHead}>
          <h2>More from {bankName}</h2>
          <p>
            Stories, tools, and programmes that reflect how we serve customers
            and communities—beyond the everyday transaction.
          </p>
        </div>
        <div className={classes.services}>
          <article className={classes.service}>
            <div className={classes.photo}>
              <img src={s1} alt="" />
            </div>
            <div className={classes.serviceContent}>
              <h3>Championing ambition—on and off the field</h3>
              <p>
                We are proud to stand beside athletes who embody resilience. Our
                partnerships help us invest in grassroots sport and financial
                education for the next generation.
              </p>
              <Link className={classes.serviceLink} to="/banking">
                Discover Premier banking
                <HiOutlineArrowRight className={classes.serviceArrow} aria-hidden />
              </Link>
            </div>
          </article>
          <article className={classes.service}>
            <div className={classes.photo}>
              <img src={s2} alt="" />
            </div>
            <div className={classes.serviceContent}>
              <h3>Navigating a higher cost of living</h3>
              <p>
                When bills rise faster than income, small changes matter. We
                publish practical steps—budgeting, benefits checks, and
                conversation starters with creditors—so you can act early.
              </p>
              <Link className={classes.serviceLink} to="/help">
                Get support &amp; guidance
                <HiOutlineArrowRight className={classes.serviceArrow} aria-hidden />
              </Link>
            </div>
          </article>
          <article className={classes.service}>
            <div className={classes.photo}>
              <img src={s3} alt="" />
            </div>
            <div className={classes.serviceContent}>
              <h3>Smarter homes, steadier bills</h3>
              <p>
                Improving insulation, heating controls, and appliances can lower
                monthly costs and add long-term value. Explore ideas that suit
                your property and budget.
              </p>
              <Link className={classes.serviceLink} to="/help">
                View ideas &amp; next steps
                <HiOutlineArrowRight className={classes.serviceArrow} aria-hidden />
              </Link>
            </div>
          </article>
        </div>
      </section>

      <section className={classes.trust}>
        <p>
          <strong>{bankName}</strong> is authorised and regulated by the Swiss
          Financial Market Supervisory Authority (FINMA). Eligibility and fees
          apply to products; read pre-contractual information before you apply.
        </p>
      </section>
    </div>
  );
};

export default HomePage;
